import cx from 'classnames'
import CSVImportIcon from 'images/csv-import-icon.svg'
import HubspotImportIcon from 'images/hubspot-logo.svg'
import ImportSourceSuggestionIcon from 'images/import-suggestion-icon.svg'
import MarketoImportIcon from 'images/marketo-import-icon.svg'
import PardotImportIcon from 'images/salesforce-pardot-logo.svg'
import {
  filter,
  map,
  pipe
} from 'ramda'
import React,
{
  Component,
  Fragment
} from 'react'
import { connect } from 'react-redux'

import {
  A,
  Card,
  Div,
  H3,
  P
} from '@driftt/tide-core'
import {
  getOrgHubspotAccessType,
  HubspotAccessTypes
} from 'utils/hubspotAccess'
import { userHasOrgIntegrationAccess } from 'utils/integrationAccess'
import {
  getOrgMarketoAccessType,
  MarketoAccessTypes
} from 'utils/marketoAccess'
import {
  getOrgPardotAccessType,
  PardotAccessTypes
} from 'utils/pardotAccess'

import ImportSourceSourceSuggestionModal from './ImportSourceSuggestionModal'

import './style.css'

const mapStateToProps = state => ({
  orgId: state.user.orgId,
  bootstrapComplete: state.config.bootstrapComplete
})

const IMPORT_TYPES = {
  MARKETO: 'MARKETO',
  PARDOT: 'PARDOT',
  HUBSPOT: 'HUBSPOT',
  CSV: 'CSV'
}

const MarketoAccessTypeExtraConfig = {
  [MarketoAccessTypes.HAS_ACCESS_CONNECTED]: {},
  [MarketoAccessTypes.HAS_ACCESS_NOT_CONNECTED]: {
    ctaText: 'Connect integration',
    url: '/settings/setup/marketo'
  },
  [MarketoAccessTypes.NO_ACCESS]: {
    disabled: true,
    upgradeText: 'Upgrade',
    ctaText: 'Upgrade to Premium',
    ctaUrl: 'https://drift.com/pricing'
  },
}

const PardotAccessTypeExtraConfig = {
  [PardotAccessTypes.HAS_ACCESS_CONNECTED]: {},
  [PardotAccessTypes.HAS_ACCESS_NOT_CONNECTED]: {
    ctaText: 'Connect integration',
    url: '/settings/setup/pardot'
  },
  [PardotAccessTypes.NO_ACCESS]: {
    disabled: true,
    upgradeText: 'Upgrade',
    ctaText: 'Upgrade to Premium',
    ctaUrl: 'https://drift.com/pricing'
  }
}

const HubspotAccessTypeExtraConfig = {
  [HubspotAccessTypes.HAS_ACCESS_CONNECTED]: {},
  [HubspotAccessTypes.HAS_ACCESS_NOT_CONNECTED]: {
    ctaText: 'Connect integration',
    url: '/settings/setup/hubspot'
  },
}

const CSV_CARD = [
  {
    type: IMPORT_TYPES.CSV,
    title: 'Import CSV for email',
    description: 'Add contacts to enroll in an email playbook',
    url: '/contacts/imports/csv',
    imgSrc: CSVImportIcon
  }
]

const INTEGRATION_CARDS = [
  {
    type: IMPORT_TYPES.MARKETO,
    title: 'Import Marketo list',
    description: 'Add segments of contacts to Drift from a Marketo list. Lists sync every 24 hours.',
    url: '/contacts/imports/marketo',
    imgSrc: MarketoImportIcon,
  },
  {
    type: IMPORT_TYPES.PARDOT,
    title: 'Import Pardot list',
    description: 'Add segments of contacts to Drift from a Pardot list. Lists sync every 24 hours.',
    url: '/contacts/imports/pardot',
    imgSrc: PardotImportIcon
  },
  {
    type: IMPORT_TYPES.HUBSPOT,
    title: 'Import HubSpot list',
    description: 'Add segments of contacts to Drift from a HubSpot list. Lists sync every 24 hours.',
    url: '/contacts/imports/hubspot',
    imgSrc: HubspotImportIcon
  }
]


class ChooseImportType extends Component {
  constructor(props) {
    super(props)

    this.state = { isImportSourceSuggestionModalOpen: false }
  }

  navigateToCardLink = (card) => {
    if (!card) {
      return
    }

    window.location.href = card.url
  }

  shouldDisplayCard = (card) => {
    if (!card) {
      return false
    }

    if (card.type === IMPORT_TYPES.HUBSPOT && getOrgHubspotAccessType() === HubspotAccessTypes.NO_ACCESS_HIDDEN) {
      return false
    }

    return true
  }

  addAdditionalCardConfig = card => {
    if (card.type === IMPORT_TYPES.MARKETO) {
      const marketoAccessType = getOrgMarketoAccessType()
      const extraConfig = MarketoAccessTypeExtraConfig[marketoAccessType]

      return {
        ...card,
        ...extraConfig
      }
    }

    if (card.type === IMPORT_TYPES.PARDOT) {
      const pardotAccessType = getOrgPardotAccessType()
      const extraConfig = PardotAccessTypeExtraConfig[pardotAccessType]

      return {
        ...card,
        ...extraConfig
      }
    }

    if (card.type === IMPORT_TYPES.HUBSPOT) {
      const hubspotAccessType = getOrgHubspotAccessType()
      const extraConfig = HubspotAccessTypeExtraConfig[hubspotAccessType]

      return {
        ...card,
        ...extraConfig
      }
    }

    return card
  }

  openImportSourceSuggestionModal = () => {
    this.setState({ isImportSourceSuggestionModalOpen: true })
  }

  closeImportSourceSuggestionModal = () => {
    this.setState({ isImportSourceSuggestionModalOpen: false })
  }

  renderSuggestImportSourceCard = () => {
    return (
      <Div className="choose-import--card-wrapper">
        <Card
          align="left"
          header={"Suggest import source"}
          description={"Don't see an import source that you want? Let our team know!"}
          imgSrc={ImportSourceSuggestionIcon}
          className="choose-import--card choose-import--card--suggestion"
          onClick={this.openImportSourceSuggestionModal}
        />
      </Div>
    )
  }

  renderImportCard = (card) => {
    // Only allow CTA to be clicked if the card is disabled
    const ctaHref = card.disabled ? card.ctaUrl : null
    const cta = (
      <P className="choose-import--card--link">
        <A href={ctaHref} target="_blank">{card.ctaText}</A>
      </P>
    )

    const hasCTA = card.ctaText

    const description = (
      <Fragment>
        {card.description}
        {hasCTA && cta}
      </Fragment>
    )
    return (
      <Div className="choose-import--card-wrapper" key={card.type}>
        <Card
          align="left"
          onClick={() => this.navigateToCardLink(card)}
          header={card.title}
          description={description}
          imgSrc={card.imgSrc}
          className={cx("choose-import--card", { "choose-pardot-import--card": card.type==="PARDOT" })}
          disabled={card.disabled}
          upgrade={!!card.upgradeText}
          upgradeText={card.upgradeText}
        />
      </Div>
    )
  }

  render() {
    if(!this.props.bootstrapComplete) {return null}

    const IMPORT_CARDS = userHasOrgIntegrationAccess() ?  [...CSV_CARD, ...INTEGRATION_CARDS] : CSV_CARD
    const importCardOptions = pipe(filter(this.shouldDisplayCard),
      map(this.addAdditionalCardConfig))(IMPORT_CARDS)

    return (
      <Div className="choose-import--wrapper">
        <H3 className="choose-import--title">Select an import source</H3>
        <Div className="choose-import--all-cards-wrapper">
          {importCardOptions.map(this.renderImportCard)}
          {this.renderSuggestImportSourceCard()}
        </Div>
        <ImportSourceSourceSuggestionModal
          isOpen={this.state.isImportSourceSuggestionModalOpen}
          onClose={this.closeImportSourceSuggestionModal}
        />
      </Div>
    )
  }
}

export default connect(mapStateToProps)(ChooseImportType)
